import React, { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, Play, Edit3, Save, Inbox } from 'react-feather';
import config from '../../../config';

import ReportShell from '../../Admin/Reports/shared/ReportShell';
import ReportDateInput from '../../Admin/Reports/shared/ReportDateInput';
import useReportLookups from '../../Admin/Reports/shared/useReportLookups';
import { buildDynamicField } from '../../Admin/Reports/shared/dynamicReportConfig';

const ManageDynamicReports = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);
    const concessionaireId = localStorage.getItem('concessionaireId');
    const login_id = localStorage.getItem('loginId');

    const [view, setView] = useState(''); // '' | 'Add' | 'Edit'
    const [reports, setReports] = useState([]);            // base report catalogue
    const [dynamicReports, setDynamicReports] = useState([]); // saved reports
    const [reportId, setReportId] = useState('');          // Add: chosen base report
    const [reportFilters, setReportFilters] = useState([]);     // Add: base report's filters
    const [selectedReport, setSelectedReport] = useState(null); // Edit: { report_id, dynamic_report_id }
    const [selectedReportFilters, setSelectedReportFilters] = useState([]); // Edit
    const [dynamicReportName, setDynamicReportName] = useState('');
    const [filterValues, setFilterValues] = useState({});
    const [saving, setSaving] = useState(false);

    // The active filter list + the key each filter writes under differ between
    // Add (keyed by filter._id) and Edit (keyed by filter.filter_id).
    const activeFilters = view === 'Edit' ? selectedReportFilters : reportFilters;
    const keyField = view === 'Edit' ? 'filter_id' : '_id';
    const nameToKey = useMemo(() => {
        const m = {};
        activeFilters.forEach((f) => { m[f.filter_name] = f[keyField]; });
        return m;
    }, [activeFilters, keyField]);

    const lookups = useReportLookups({
        propertyId: filterValues[nameToKey.Property],
        categoryId: filterValues[nameToKey.Category],
        subcategoryId: filterValues[nameToKey.Subcategory],
        groupName: filterValues[nameToKey.Group],
    });

    const fetchDynamicReports = () => {
        axios.post(`${config.hostname}/dynamicreports/getalldynamicreport`, { concessionaire_id: concessionaireId }, { headers })
            .then((res) => setDynamicReports(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.error('load dynamic reports failed', err));
    };

    useEffect(() => {
        axios.get(`${config.hostname}/dynamicreports/getreports`, { headers })
            .then((res) => setReports(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.error('load base reports failed', err));
        fetchDynamicReports();
    }, []);

    const setFV = (key, value) => setFilterValues((prev) => ({ ...prev, [key]: value }));

    const renderField = (field) => {
        const val = filterValues[field.key] || '';
        if (field.type === 'date') {
            return (
                <ReportDateInput
                    id={field.key}
                    value={val}
                    max={field.max}
                    placeholder={field.placeholder}
                    onChange={(next) => setFV(field.key, next)}
                />
            );
        }
        if (field.type === 'text') {
            return (
                <input className="v3-input" type="text" value={val}
                    placeholder={field.placeholder ? t(field.placeholder) : ''}
                    onChange={(e) => setFV(field.key, e.target.value)} />
            );
        }
        return (
            <div className="v3-select-wrap">
                <select className="v3-input v3-select" value={val} onChange={(e) => setFV(field.key, e.target.value)}>
                    <option value="">{t(field.placeholder || 'All')}</option>
                    {(field.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
        );
    };

    const filterFields = useMemo(
        () => activeFilters.map((f) => buildDynamicField(f.filter_name, f[keyField], t, lookups)),
        [activeFilters, keyField, t, lookups],
    );

    // ── Navigation between views ─────────────────────────────────────────
    const openAdd = () => {
        setView('Add');
        setReportId('');
        setReportFilters([]);
        setDynamicReportName('');
        setFilterValues({});
    };

    const backToList = () => {
        setView('');
        setSelectedReport(null);
        setReportFilters([]);
        setSelectedReportFilters([]);
        setDynamicReportName('');
        setFilterValues({});
    };

    const onBaseReportChange = (value) => {
        setReportId(value);
        setFilterValues({});
        if (!value) { setReportFilters([]); return; }
        axios.post(`${config.hostname}/dynamicreports/getfiltersbyreport`, { report_id: value }, { headers })
            .then((res) => setReportFilters(Array.isArray(res.data) ? res.data : []))
            .catch((err) => { console.error('load filters failed', err); setReportFilters([]); });
    };

    const openEdit = (report_id, dynamic_report_id) => {
        axios.post(`${config.hostname}/dynamicreports/getdynamicreport`, { report_id, dynamic_report_id }, { headers })
            .then((res) => {
                setSelectedReport(res.data.report);
                setDynamicReportName(res.data.report.dynamic_report_name);
                setSelectedReportFilters(res.data.filters || []);
                const init = {};
                (res.data.filters || []).forEach((f) => { init[f.filter_id] = f.filter_value; });
                setFilterValues(init);
                setView('Edit');
            })
            .catch((err) => { console.error(err); toast.error(t('Error fetching report details')); });
    };

    const runReport = (report_id, dynamic_report_id) =>
        navigate(`${process.env.PUBLIC_URL}/admin/runreport/${report_id}?dynamic_report_id=${dynamic_report_id}`);

    // ── Persist ──────────────────────────────────────────────────────────
    const saveNewReport = (e) => {
        e.preventDefault();
        setSaving(true);
        const data = {
            concessionaire_id: concessionaireId,
            login_id,
            report_id: reportId,
            dynamic_report_name: dynamicReportName,
            reportFilters: reportFilters
                .map((f) => ({ _id: f._id, filter_value: filterValues[f._id] || '' }))
                .filter((f) => f.filter_value !== ''),
        };
        axios.post(`${config.hostname}/dynamicreports/savenewreport`, data, { headers })
            .then(() => { toast.success(t('Report Added Successfully')); fetchDynamicReports(); backToList(); })
            .catch((err) => { console.error(err); toast.error(t('Error Adding Report')); })
            .finally(() => setSaving(false));
    };

    const updateReport = (e) => {
        e.preventDefault();
        setSaving(true);
        const validFilters = selectedReportFilters
            .filter((f) => filterValues[f.filter_id])
            .map((f) => ({ filter_id: f.filter_id, filter_value: filterValues[f.filter_id], filter_name: f.filter_name }));
        const data = {
            report_id: selectedReport.report_id,
            dynamic_report_id: selectedReport.dynamic_report_id,
            dynamic_report_name: dynamicReportName,
            reportFilters: validFilters,
            concessionaire_id: concessionaireId,
            login_id,
        };
        axios.post(`${config.hostname}/dynamicreports/updatedynamicreport`, data, { headers })
            .then(() => { toast.success(t('Report Updated Successfully')); fetchDynamicReports(); backToList(); })
            .catch((err) => { console.error(err); toast.error(t('Error Updating Report')); })
            .finally(() => setSaving(false));
    };

    // ── Render ───────────────────────────────────────────────────────────
    const FieldsGrid = () => (
        <div className="v3-sadmin-form-grid-3">
            {filterFields.map((field) => (
                <div className="v3-report-field" key={field.key}>
                    <span className="v3-label">{t(field.label)}</span>
                    {renderField(field)}
                </div>
            ))}
        </div>
    );

    if (view === 'Add') {
        return (
            <ReportShell
                crumb="Dynamic Reports"
                title={t('Add New Report')}
                subtitle={t('Pick a base report, set its default filters and save it as a reusable report.')}
                actions={
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={backToList}>
                        <ArrowLeft size={14} /> {t('Back')}
                    </button>
                }
            >
                <form className="v3-card v3-report-filters" onSubmit={saveNewReport}>
                    <div className="v3-sadmin-form-grid-3">
                        <div className="v3-report-field">
                            <label className="v3-label" htmlFor="base-report">{t('Reports')}</label>
                            <div className="v3-select-wrap">
                                <select id="base-report" className="v3-input v3-select" value={reportId} required
                                    onChange={(e) => onBaseReportChange(e.target.value)}>
                                    <option value="">{t('Select Report')}</option>
                                    {reports.map((r) => (
                                        <option key={r._id} value={r._id}>{r.report_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {reportFilters.length > 0 && (
                        <Fragment>
                            <FieldsGrid />
                            <div className="v3-sadmin-form-grid-3">
                                <div className="v3-report-field" style={{ gridColumn: 'span 2' }}>
                                    <label className="v3-label" htmlFor="new-report-name">{t('Save New report As')}</label>
                                    <input id="new-report-name" className="v3-input" type="text" required
                                        placeholder={t('Report Name')}
                                        value={dynamicReportName}
                                        onChange={(e) => setDynamicReportName(e.target.value)} />
                                </div>
                            </div>
                            <div className="v3-report-filter-actions">
                                <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                                    <Save size={14} /> {saving ? t('Saving…') : t('Save Report')}
                                </button>
                            </div>
                        </Fragment>
                    )}
                </form>
            </ReportShell>
        );
    }

    if (view === 'Edit') {
        return (
            <ReportShell
                crumb="Dynamic Reports"
                title={t('Edit Report')}
                subtitle={t('Update the name or default filters for this saved report.')}
                actions={
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={backToList}>
                        <ArrowLeft size={14} /> {t('Back')}
                    </button>
                }
            >
                <form className="v3-card v3-report-filters" onSubmit={updateReport}>
                    <div className="v3-sadmin-form-grid-3">
                        <div className="v3-report-field" style={{ gridColumn: 'span 2' }}>
                            <label className="v3-label" htmlFor="edit-report-name">{t('Report Name')}</label>
                            <input id="edit-report-name" className="v3-input" type="text" required
                                value={dynamicReportName}
                                onChange={(e) => setDynamicReportName(e.target.value)} />
                        </div>
                    </div>
                    {selectedReportFilters.length > 0 && (
                        <Fragment>
                            <FieldsGrid />
                            <div className="v3-report-filter-actions">
                                <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                                    <Save size={14} /> {saving ? t('Saving…') : t('Update Report')}
                                </button>
                            </div>
                        </Fragment>
                    )}
                </form>
            </ReportShell>
        );
    }

    // List view
    return (
        <ReportShell
            crumb="Dynamic Reports"
            title={t('Dynamic Reports')}
            subtitle={t('Saved reports with preset filters — run or edit them anytime.')}
            actions={
                <button type="button" className="v3-btn v3-btn-primary" onClick={openAdd}>
                    <Plus size={14} /> {t('Add New')}
                </button>
            }
        >
            <div className="v3-card v3-report-table-card">
                {dynamicReports.length === 0 ? (
                    <div className="v3-report-state">
                        <span className="v3-report-state-icon"><Inbox size={20} /></span>
                        <h3>{t('No saved reports')}</h3>
                        <p>{t('Create one with “Add New” to get a reusable, preset report.')}</p>
                    </div>
                ) : (
                    <div className="v3-sadmin-table-wrap">
                        <table className="v3-sadmin-table v3-report-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 64 }}>{t('No.')}</th>
                                    <th>{t('Report Name')}</th>
                                    <th className="num" style={{ width: 200 }}>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dynamicReports.map((item, index) => (
                                    <tr key={item.dynamic_report_id || index}>
                                        <td>{index + 1}</td>
                                        <td>{item.dynamic_report_name}</td>
                                        <td className="num">
                                            <div className="v3-dr-row-actions">
                                                <button type="button" className="v3-btn v3-btn-ghost"
                                                    onClick={() => openEdit(item.report_id, item.dynamic_report_id)}>
                                                    <Edit3 size={13} /> {t('Edit')}
                                                </button>
                                                <button type="button" className="v3-btn v3-btn-primary"
                                                    onClick={() => runReport(item.report_id, item.dynamic_report_id)}>
                                                    <Play size={13} /> {t('Run')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ReportShell>
    );
};

export default ManageDynamicReports;
